import { Box, useTheme } from "@mui/material";
import { type FC, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbarBottomInset } from "../../../../contexts/SnackbarContext";
import { BOTTOM_NAV, LAYOUT_OFFSET, SHEET_STEP, SNACKBAR_STACK } from "../../../../theme/tokens";
import { SystemSideDrawer } from "../../../UIToolkit/Panels/SystemSideDrawer";
import { InHouseMobileSheet } from "../../../UIToolkit/Popups/InHouseMobileSheet";
import { SheetBackContext } from "../../../UIToolkit/Popups/SheetBackContext";

type Props = {
	open: boolean;
	onClose: () => void;
	/** `gaps-<sheet>-sheet`. Lands on MUI's full-viewport Modal root — measure `.MuiDrawer-paper` inside it. */
	dataTestId: string;
	/** One `SheetStep`, swapped in place as the flow moves on. */
	children: ReactNode;
	/** A reading step can occupy the viewport and cover the bottom navigation. */
	fullScreen?: boolean;
	/**
	 * One height for every step (`SHEET_STEP.FIXED_HEIGHT_DVH`, capped by the room above the bar) instead of sizing to
	 * each, so a sheet whose pages differ a lot does not jump as they turn — the Edit Shift sheet (APP-886).
	 */
	fixedHeight?: boolean;
	/** Drag down from the handle or a step's heading to close (`InHouseMobileSheet` `dragHandleToClose`); the body still scrolls. */
	dragToClose?: boolean;
	/**
	 * Open as a full page above the bottom bar instead of a sheet (APP-886: Post a shift and Add staff): it slides in from
	 * the right, fills the screen above the bar, and each step's heading leads with ‹ back. `label` names the page for
	 * assistive tech, since the step draws the visible title.
	 */
	page?: { label: string };
};

/**
 * The gaps-first home's action-sheet chrome: an `InHouseMobileSheet` that stops above the bottom bar, sized to its
 * content and allowed the whole screen above the bar when it needs it, with its heading and footer drawn by the
 * `SheetStep` inside it rather than by the sheet (each step has its own question and its own buttons).
 *
 * Never swipeable over the whole paper: `swipeToClose` makes `SwipeableDrawer` cancel every touchmove over it, and
 * these sheets scroll (a unit list, a staff search). The scrim, Escape and every step's own footer close them; a sheet
 * that opts into `dragToClose` also closes from its handle and heading only, which leaves the body scrolling.
 */
export const GapsSheetFrame: FC<Props> = ({
	open,
	onClose,
	dataTestId,
	children,
	fullScreen,
	// DEFAULT: off — every other sheet keeps sizing to its content, and keeps no drag gesture.
	fixedHeight = false,
	dragToClose = false,
	page,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const paperHeight = useOpenPaperHeight(open);
	const content = useContentAsLastOpen(open, children);
	const roomAboveBar = `calc(100dvh - ${BOTTOM_NAV.TOTAL_HEIGHT_CSS} - ${LAYOUT_OFFSET.SHEET_TOP_GAP_CSS})`;
	const isFixed = fixedHeight && fullScreen !== true;
	// While the sheet is up, a toast (a save's "placed in…", an add's "added") lands in the dimmed band ABOVE it, never
	// on its footer's buttons; once it closes, toasts fall back to sitting above the bar. Capped so a burst of them (an
	// add that also closes a request raises three) still fits above the lift on a sheet that fills the screen.
	useSnackbarBottomInset(
		open && paperHeight.height !== null && fullScreen !== true
			? `min(calc(${BOTTOM_NAV.TOTAL_HEIGHT_CSS} + ${paperHeight.height}px), calc(100dvh - ${SNACKBAR_STACK.STACK_STEP_PX * SNACKBAR_STACK.KEEP_ON_SCREEN}px))`
			: null,
	);

	// A layout-neutral hook into the paper, so its height can be followed as the steps change it. Inert while the drawer
	// animates out: a closing sheet — closed by the user, by a hand-on, or because the home's day moved under it — keeps
	// drawing its content, and none of it may take a tap on the way down.
	const body = (
		<Box ref={paperHeight.anchorRef} inert={!open} sx={{ display: "contents" }}>
			{content}
		</Box>
	);

	if (page !== undefined) {
		return (
			<SystemSideDrawer
				open={open}
				onClose={onClose}
				hideHeader
				dialogLabel={page.label}
				bottomOffset={BOTTOM_NAV.TOTAL_HEIGHT_CSS}
				bodySx={{ p: 0 }}
				dataTestId={dataTestId}
			>
				<SheetBackContext.Provider value={{ onBack: onClose, label: t("back") }}>{body}</SheetBackContext.Provider>
			</SystemSideDrawer>
		);
	}

	return (
		<InHouseMobileSheet
			open={open}
			onClose={onClose}
			showHeader={false}
			fitContent={fullScreen !== true && !isFixed}
			fullViewport={fullScreen === true || isFixed}
			height={fullScreen === true ? "100dvh" : isFixed ? `min(${SHEET_STEP.FIXED_HEIGHT_DVH}dvh, ${roomAboveBar})` : undefined}
			maxHeight={fullScreen === true ? "100dvh" : roomAboveBar}
			dragHandleToClose={dragToClose && fullScreen !== true}
			bottomOffset={fullScreen === true ? undefined : BOTTOM_NAV.TOTAL_HEIGHT_CSS}
			zIndex={fullScreen === true ? theme.zIndex.modal : undefined}
			paperSx={fullScreen === true ? { borderRadius: 0 } : undefined}
			contentSx={{ p: 0, overflowY: fullScreen === true ? "hidden" : "auto" }}
			dataTestId={dataTestId}
		>
			{body}
		</InHouseMobileSheet>
	);
};

/**
 * The content to draw: the live content while the sheet is open, and — while it animates out — the content exactly as
 * it last committed OPEN. A sheet closes because the page moved on (another day under a day-bound sheet, a hand-on, a
 * request that left the list), and the content its host builds on the way down describes where the page moved TO: an add
 * sheet opened for Thursday read "Fri 2 Oct" for its whole exit. The same element object, rendered again, is not
 * re-rendered by React, so the closing sheet keeps its own words; its subtree still follows its own state and context.
 *
 * A ref, written after each open commit and read only while closed: the content of the last OPEN commit has to be
 * available on the first closed render, which runs before any effect of its own could capture it.
 */
const useContentAsLastOpen = (open: boolean, children: ReactNode): ReactNode => {
	const lastOpenRef = useRef<ReactNode>(children);
	useLayoutEffect(() => {
		if (open) lastOpenRef.current = children;
	});
	return open ? children : lastOpenRef.current;
};

/**
 * The open sheet's paper height, followed as it changes (steps swap, a list grows); `null` while closed.
 *
 * Found through a callback ref rather than on the `open` change: the drawer portals its paper, which can mount a render
 * after `open` flips, and a lookup made at that moment would find nothing and never look again.
 */
const useOpenPaperHeight = (open: boolean) => {
	const [paper, setPaper] = useState<HTMLElement | null>(null);
	const [height, setHeight] = useState<number | null>(null);
	const anchorRef = useCallback((node: HTMLDivElement | null) => {
		const found = node?.closest(".MuiDrawer-paper");
		setPaper(found instanceof HTMLElement ? found : null);
	}, []);
	useEffect(() => {
		if (!open || paper === null) {
			setHeight(null);
			return;
		}
		const observer = new ResizeObserver(() => setHeight(Math.round(paper.getBoundingClientRect().height)));
		observer.observe(paper);
		return () => observer.disconnect();
	}, [open, paper]);
	return { anchorRef, height };
};
